/**
 * Shared field-visibility predicate for user-input form fields.
 *
 * Both UserInputFormRenderer (render layer) and useRunAgentUserInput (gate layer) must
 * agree on which fields are "visible" so that the Run-Report gate never blocks on a field
 * the user cannot see or fix. Extract the logic here so it cannot diverge. (guards LSP-16496)
 */

interface VisibleWhenCondition {
	field: string;
	operator: string;
	value: any;
}

export interface UserInputFieldDefinition {
	key: string;
	type: string;
	hidden?: boolean;
	visibleWhen?: VisibleWhenCondition;
	[key: string]: any;
}

/**
 * Compare two primitive values loosely so string-vs-number mismatches do not cause false
 * negatives. Number fields store their value as a string (type="text", LSP-16496) but a schema
 * condition may supply a numeric literal (e.g. value: 1990). Compare by String coercion;
 * null/undefined only match each other; objects fall back to strict identity.
 */
export const valuesLooselyEqual = (left: any, right: any): boolean => {
	if (left === right) return true;
	if (left == null || right == null) return false;
	if (typeof left === "object" || typeof right === "object") return false;
	return String(left) === String(right);
};

/**
 * Returns true when a field should be visible given the current form values.
 * Mirrors the `visibleFields` filter in UserInputFormRenderer.vue exactly.
 */
export const isUserInputFieldVisible = (field: UserInputFieldDefinition, formValues: Record<string, any>): boolean => {
	if (field.hidden) return false;
	if (!field.visibleWhen) return true;

	const condition = field.visibleWhen;
	const sourceValue = formValues[condition.field];

	switch (condition.operator) {
		case "equals":
			return valuesLooselyEqual(sourceValue, condition.value);
		case "notEquals":
			return !valuesLooselyEqual(sourceValue, condition.value);
		case "contains":
			return Array.isArray(sourceValue)
				? sourceValue.includes(condition.value)
				: String(sourceValue || "").includes(String(condition.value || ""));
		case "notEmpty":
			return sourceValue !== null && sourceValue !== undefined && sourceValue !== "" &&
				(!Array.isArray(sourceValue) || sourceValue.length > 0);
		default:
			return true;
	}
};
